import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ImageUp, Loader2, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { inputCls, btnPrimary, useApi, Skeleton } from "@/components/portal/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { RESOURCES } from "@/pages/admin/resources";
import ParticipantsDialog from "@/pages/admin/ParticipantsDialog";
import { RichText } from "@/components/RichText";

export function ImageField({ value, onChange, id, hint }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?public=true", fd);
      onChange(data.url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex-1">
      <div className="flex gap-3 items-center">
        {value ? <img src={fileUrl(value)} alt="" className="w-16 h-16 rounded-xl object-cover border border-slate-100" /> : <span className="w-16 h-16 rounded-xl bg-slate-100" />}
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL gambar atau unggah" data-testid={`field-${id}`} />
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" onClick={() => ref.current?.click()} data-testid={`field-${id}-upload`} className="shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-blue-600 grid place-items-center hover:bg-blue-100">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageUp className="w-5 h-5" />}</button>
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">{hint || "Unggah gambar resolusi tinggi. Hindari menaruh objek penting di tepi agar tidak terpotong."}</p>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const [key, , type, options] = field;
  if (type === "textarea") return <textarea rows={4} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`} />;
  if (type === "richtext") return <RichText value={value} onChange={onChange} id={key} />;
  if (type === "select") return <select className={inputCls} value={value || options[0]} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`}>{options.map((o) => <option key={o}>{o}</option>)}</select>;
  if (type === "bool") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${key}`} />;
  if (type === "image") return <ImageField value={value} onChange={onChange} id={key} />;
  return <input type={type} className={inputCls} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`field-${key}`} />;
}

function Cell({ k, v }) {
  if (k === "image_url") return <img src={fileUrl(v)} alt="" className="w-16 h-12 rounded-lg object-cover" />;
  return <span className={k === "title" || k === "name" ? "font-bold text-slate-900" : "text-slate-600"}>{String(v ?? "—")}</span>;
}

export default function ResourceManager() {
  const { resource } = useParams();
  const cfg = RESOURCES[resource];
  const { data, reload } = useApi(`/admin/r/${resource}`);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [participantsOf, setParticipantsOf] = useState(null);
  if (!cfg) return <p>Resource tidak dikenal.</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/r/${resource}/${editing.id}`, editing);
      else await api.post(`/admin/r/${resource}`, editing);
      toast.success("Tersimpan");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (item) => {
    if (!window.confirm("Hapus data ini?")) return;
    await api.delete(`/admin/r/${resource}/${item.id}`);
    toast.success("Dihapus");
    reload();
  };

  return (
    <div data-testid={`admin-resource-${resource}`}>
      <AdminTitle eyebrow={cfg.eyebrow} title={cfg.title}>
        <button onClick={() => setEditing({ ...(cfg.defaults || {}) })} data-testid="resource-add-button" className={btnPrimary}><Plus className="w-4 h-4" />Tambah</button>
      </AdminTitle>
      {!data ? <Skeleton className="h-80" /> : (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-x-auto">
          <table className="w-full text-sm" data-testid="resource-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>{cfg.columns.map((c) => <th key={c} className="px-5 py-4 font-bold">{cfg.fields.find((f) => f[0] === c)?.[1]}</th>)}<th className="px-5 py-4" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/60" data-testid={`resource-row-${item.id}`}>
                  {cfg.columns.map((c) => <td key={c} className="px-5 py-4"><Cell k={c} v={item[c]} /></td>)}
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    {resource === "events" && <button onClick={() => setParticipantsOf(item)} data-testid={`event-participants-${item.id}`} className="h-9 px-3 rounded-xl hover:bg-emerald-50 text-emerald-600 inline-flex items-center gap-1.5 text-xs font-bold"><Users className="w-4 h-4" />Peserta</button>}
                    <button onClick={() => setEditing(item)} data-testid={`resource-edit-${item.id}`} className="w-9 h-9 rounded-xl hover:bg-blue-50 text-blue-600 inline-grid place-items-center"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(item)} data-testid={`resource-delete-${item.id}`} className="w-9 h-9 rounded-xl hover:bg-red-50 text-red-600 inline-grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.length === 0 && <p className="p-8 text-center text-slate-500">Belum ada data.</p>}
        </div>
      )}
      <ParticipantsDialog event={participantsOf} onClose={() => setParticipantsOf(null)} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Tambah"} {cfg.title}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              {cfg.fields.map((f) => (
                <label key={f[0]} className="block">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{f[1]}</span>
                  <div className="mt-2"><FieldInput field={f} value={editing[f[0]]} onChange={(v) => setEditing({ ...editing, [f[0]]: v })} /></div>
                </label>
              ))}
              <button disabled={saving} data-testid="resource-save-button" className={`${btnPrimary} w-full`}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
